// ProductDetail.jsx — fiche produit : héros, statistiques, infos, code-barres, actions.
import React from "react";
import {
  Ico, useIcons, fmt, productStatus, thumbStyle, initials,
  isRawMaterial, labelOf, STOCK_MODE_OPTS, BARCODE_OPTS,
} from "./productsShared";
import { nameForShort } from "./productData";
import API_CONFIG from "../../config/api";

// Résout l'URL d'affichage : URL absolue en prod (Spaces), chemin relatif en dev local.
const resolveImg = (img) =>
  !img
    ? null
    : String(img).startsWith("http")
    ? img
    : `${API_CONFIG.BASE_URL}${String(img).startsWith("/") ? "" : "/"}${img}`;

function InfoRow({ ico, label, val }) {
  if (val == null || val === "") return null;
  return (
    <div className="dl-row">
      <span className="dl-k"><Ico name={ico} />{label}</span>
      <span className="dl-v">{val}</span>
    </div>
  );
}

export default function ProductDetail({
  product: p,
  onBack,
  onMore,
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
  canManageStatus = true,
}) {
  useIcons();
  if (!p) return null;
  const st = productStatus(p);
  const marge = p.sale - p.cost;
  const margePct = p.sale ? Math.round((marge / p.sale) * 100) : 0;
  const unitName = nameForShort(p.unit);
  const stockModeLabel = labelOf(STOCK_MODE_OPTS, p.stockMode);
  const showMode = p.stockMode && p.stockMode !== "standard";
  const stColor = st.k === "ok" ? "green" : st.k === "low" ? "amber" : "red";

  return (
    <div className="screen screen--in" data-pid={p.id}>
      <div className="body" style={{ paddingBottom: 0 }}>
        <div className="hero">
          <div className="hero__img" style={thumbStyle(p.cat)}>
            {p.image ? <img src={resolveImg(p.image)} alt="" /> : initials(p.name)}
          </div>
          <div className="hero__nav">
            <button className="iconbtn" onClick={onBack}><Ico name="arrow-left" /></button>
            {canEdit || canDelete || canManageStatus ? (
              <button className="iconbtn" onClick={onMore}><Ico name="more-horizontal" /></button>
            ) : null}
          </div>
          <div className="hero__pill">
            <span className={"pill " + st.cls}><span className="dot" />{st.label}</span>
          </div>
        </div>

        <div className="dsheet">
          <div className="dtitle">
            <h2>{p.name}</h2>
            <span className="dtitle__code">{p.code}</span>
          </div>

          <div className="dmeta">
            <span className="tagchip">
              <Ico name={isRawMaterial(p.type) ? "boxes" : "package-check"} />{p.type}
            </span>
            <span className="tagchip"><Ico name="tag" />{p.cat}</span>
            <span className="tagchip"><Ico name="ruler" />{unitName}</span>
            {showMode ? (
              <span className="tagchip tagchip--accent">
                <Ico name="layers" />{stockModeLabel}
              </span>
            ) : null}
          </div>

          {p.desc ? <p className="ddesc">{p.desc}</p> : null}

          <div className="stats">
            <div className="stat">
              <div className="stat__l">Stock actuel</div>
              <div className="stat__v">{fmt(p.stock)} <small>{p.unit}</small></div>
              <div className="stat__sub" style={{ color: `var(--${stColor})` }}>
                Seuil d'alerte : {fmt(p.alert)}
              </div>
            </div>
            <div className="stat stat--accent">
              <div className="stat__l">Prix de vente</div>
              <div className="stat__v">{fmt(p.sale)} <small>F</small></div>
              <div className="stat__sub" style={{ color: "var(--blue-l)" }}>
                Achat : {fmt(p.cost)} F
              </div>
            </div>
            <div className="stat">
              <div className="stat__l">Marge unitaire</div>
              <div className="stat__v">{fmt(marge)} <small>F</small></div>
              <div className="stat__sub" style={{ color: "var(--green)" }}>
                +{margePct} % du prix
              </div>
            </div>
            <div className="stat">
              <div className="stat__l">Valeur du stock</div>
              <div className="stat__v">{fmt((p.stock * p.cost) / 1000)}<small> kF</small></div>
              <div className="stat__sub" style={{ color: "var(--muted)" }}>au prix d'achat</div>
            </div>
          </div>

          <div className="section-h">Informations</div>
          <div className="dlist">
            <InfoRow ico="warehouse" label="Établissement" val={p.wh} />
            <InfoRow ico="percent" label="Taxe applicable" val={p.tax} />
            {p.pk ? (
              <InfoRow
                ico="boxes" label="Conditionnement"
                val={`${p.pk.label} · ${fmt(p.pk.price)} F`}
              />
            ) : null}
          </div>

          <div className="section-h">Code-barres</div>
          <div className="barcode">
            <div className="barcode__lines" />
            <div className="barcode__num">{p.bc}</div>
            <div className="barcode__sym">{labelOf(BARCODE_OPTS, p.bcSymbol)}</div>
          </div>

          <div className="section-h">Actions</div>
          <div className="actions">
            {canEdit ? (
              <button className="act" onClick={onEdit}>
                <Ico name="pencil" />Modifier la fiche<Ico name="chevron-right" className="chev" />
              </button>
            ) : null}
            <button className="act" onClick={onMore}>
              <Ico name="printer" />Imprimer le code-barres<Ico name="chevron-right" className="chev" />
            </button>
            {canManageStatus ? (
              <button className="act danger" onClick={onMore}>
                <Ico name="ban" />Gérer le statut<Ico name="chevron-right" className="chev" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="detail-cta">
        {canDelete ? (
          <button className="btn btn--danger-ghost btn--flex" onClick={onDelete}>
            <Ico name="trash-2" />Supprimer
          </button>
        ) : null}
        {canEdit ? (
          <button className="btn btn--primary btn--flex" onClick={onEdit}>
            <Ico name="pencil" />Modifier
          </button>
        ) : null}
      </div>
    </div>
  );
}
